'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Check, ChevronRight, ChevronLeft, Building2, CreditCard, Rocket, Globe, Server, Shield, Zap, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '../../../lib/supabase/client';
import { useRouter } from 'next/navigation';

interface SignUpProps {
  onSwitchToSignIn: () => void;
}

interface Step1Data {
  companyName: string;
  subdomain: string;
  businessType: string;
  phone: string;
  country: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const plans = [
  { id: 'plan-starter', tier: 'starter', name: 'Starter', price: '$49', period: '/mo', drivers: 10, bookings: 500, features: ['Passenger App', 'Driver App', 'Basic Dispatch'] },
  { id: 'plan-pro', tier: 'professional', name: 'Professional', price: '$149', period: '/mo', drivers: 50, bookings: 5000, features: ['Everything in Starter', 'White Label', 'Promo Codes', 'Reports'], popular: true },
  { id: 'plan-business', tier: 'business', name: 'Business', price: '$349', period: '/mo', drivers: 200, bookings: 25000, features: ['Everything in Pro', 'API Access', 'Corporate Accounts', 'Priority Support'] },
  { id: 'plan-enterprise', tier: 'enterprise', name: 'Enterprise', price: '$799', period: '/mo', drivers: 9999, bookings: 999999, features: ['Everything in Business', 'SLA', 'Custom Integrations', 'Onboarding Manager'] },
];

const businessTypes = ['Taxi Company', 'Airport Transfer', 'Limousine Service', 'Chauffeur Service', 'Corporate Transport', 'Wedding Cars', 'Mixed Fleet'];
const countries = ['United Kingdom', 'United States', 'Canada', 'Australia', 'Germany', 'France', 'UAE', 'Singapore', 'Ireland', 'Netherlands'];

const PROVISION_STEPS = [
  { id: 'account', label: 'Creating your account', icon: Shield },
  { id: 'company', label: 'Setting up company profile', icon: Building2 },
  { id: 'apps', label: 'Provisioning driver & passenger apps', icon: Globe },
  { id: 'backoffice', label: 'Configuring back office', icon: Server },
  { id: 'demo', label: 'Loading demo data', icon: Zap },
  { id: 'ready', label: 'Your platform is ready!', icon: Rocket },
];

function StripePaymentForm({ clientSecret, onSuccess, planName }: { clientSecret: string; onSuccess: () => void; planName: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/company-admin-dashboard` },
      redirect: 'if_required',
    });

    if (error) {
      toast.error(error.message ?? 'Payment setup failed. Please try again.');
      setProcessing(false);
      return;
    }

    toast.success(`${planName} plan activated! Your 14-day trial starts now.`);
    onSuccess();
    setProcessing(false);
  };

  return (
    <form onSubmit={handlePay} className="space-y-5">
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="submit"
        disabled={!stripe || processing}
        className="btn-primary w-full py-3 text-sm font-semibold"
        style={{ minHeight: '44px' }}
      >
        {processing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Processing...
          </span>
        ) : (
          'Start 14-Day Free Trial'
        )}
      </button>
      <p className="text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>
        No charge during trial. Cancel anytime before trial ends.
      </p>
    </form>
  );
}

export default function SignUpForm({ onSwitchToSignIn }: SignUpProps) {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState('plan-pro');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [formData, setFormData] = useState<Step1Data | null>(null);
  const [provisionStep, setProvisionStep] = useState(-1);
  const [provisionDone, setProvisionDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
  } = useForm<Step1Data>({
    defaultValues: {
      companyName: '',
      subdomain: '',
      businessType: 'Taxi Company',
      phone: '',
      country: 'United Kingdom',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const companyName = watch('companyName');
  const subdomain = watch('subdomain');
  const password = watch('password');

  const subdomainPreview = subdomain
    ? subdomain.toLowerCase().replace(/[^a-z0-9-]/g, '-')
    : companyName.toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 20);

  const selectedPlanObj = plans.find((p) => p.id === selectedPlan);

  const goNext = async () => {
    if (step === 2) {
      const valid = await trigger(['companyName', 'subdomain', 'businessType', 'phone', 'country', 'email', 'password', 'confirmPassword']);
      if (!valid) return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const runProvisioningAnimation = async () => {
    for (let i = 0; i < PROVISION_STEPS.length; i++) {
      setProvisionStep(i);
      await new Promise(r => setTimeout(r, 900));
    }
    setProvisionDone(true);
  };

  const onSubmitStep2 = async (data: Step1Data) => {
    setLoading(true);
    setFormData(data);
    try {
      const supabase = createClient();
      const { data: result, error } = await supabase.functions.invoke('create-subscription', {
        body: {
          planTier: selectedPlanObj?.tier ?? 'professional',
          companyName: data.companyName,
          subdomain: data.subdomain || subdomainPreview,
          businessType: data.businessType,
          phone: data.phone,
          country: data.country,
          email: data.email,
          password: data.password,
        },
      });

      if (error) throw new Error(error.message);
      if (result?.error) throw new Error(result.error);

      if (result?.clientSecret) {
        setClientSecret(result.clientSecret);
        setStep(3);
      } else {
        // No payment needed — go straight to provisioning
        setStep(4);
        runProvisioningAnimation();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Setup failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setStep(4);
    runProvisioningAnimation();
  };

  const steps = [
    { id: 'step-plan', num: 1, label: 'Choose Plan', icon: CreditCard },
    { id: 'step-details', num: 2, label: 'Business Details', icon: Building2 },
    { id: 'step-payment', num: 3, label: 'Payment', icon: CreditCard },
    { id: 'step-launch', num: 4, label: 'Launch', icon: Rocket },
  ];

  return (
    <div className="fade-in">
      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {steps.map((s, idx) => {
          const StepIcon = s.icon;
          const isComplete = step > s.num;
          const isActive = step === s.num;
          return (
            <React.Fragment key={s.id}>
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200"
                  style={{
                    background: isComplete ? '#10b981' : isActive ? 'var(--primary)' : 'var(--muted)',
                    color: isComplete || isActive ? '#000' : 'var(--muted-foreground)',
                  }}
                >
                  {isComplete ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                </div>
                <span className="text-2xs font-medium whitespace-nowrap" style={{ color: isActive ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                  {s.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className="flex-1 h-px mx-2 mb-5 transition-all duration-300"
                  style={{ background: step > s.num ? '#10b981' : 'var(--border)' }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1: Plan selection */}
      {step === 1 && (
        <div className="space-y-3 fade-in">
          <div className="mb-4">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Choose your plan</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>All plans include a 14-day free trial. Cancel anytime.</p>
          </div>
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className="relative rounded-xl p-4 cursor-pointer transition-all duration-150"
              style={{
                border: selectedPlan === plan.id ? '1px solid var(--primary)' : '1px solid var(--border)',
                background: selectedPlan === plan.id ? 'rgba(245,158,11,0.06)' : 'var(--card)',
              }}
            >
              {plan.popular && (
                <div className="absolute -top-2.5 left-4 px-2.5 py-0.5 rounded-full text-2xs font-bold" style={{ background: 'var(--primary)', color: '#000' }}>
                  MOST POPULAR
                </div>
              )}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ borderColor: selectedPlan === plan.id ? 'var(--primary)' : 'var(--border)' }}
                  >
                    {selectedPlan === plan.id && <div className="w-2 h-2 rounded-full" style={{ background: 'var(--primary)' }} />}
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{plan.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                      Up to {plan.drivers === 9999 ? 'Unlimited' : plan.drivers} drivers · {plan.bookings === 999999 ? 'Unlimited' : plan.bookings.toLocaleString()} bookings/mo
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="font-mono font-bold text-lg tabular-nums" style={{ color: 'var(--foreground)' }}>{plan.price}</span>
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{plan.period}</span>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 pl-7">
                {plan.features.map((f) => (
                  <span key={`${plan.id}-feat-${f}`} className="text-2xs px-2 py-0.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                    {f}
                  </span>
                ))}
              </div>
            </div>
          ))}
          <button onClick={goNext} className="btn-primary w-full mt-4 py-3 flex items-center justify-center gap-2">
            Continue with {selectedPlanObj?.name} Plan
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Step 2: Business details */}
      {step === 2 && (
        <form onSubmit={handleSubmit(onSubmitStep2)} className="space-y-4 fade-in">
          <div className="mb-2">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Business Details</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>Your company profile and admin account.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label-field">Company Name</label>
              <input
                {...register('companyName', { required: 'Company name is required' })}
                placeholder="City Cabs London"
                className={`input-field ${errors.companyName ? 'error' : ''}`}
              />
              {errors.companyName && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.companyName.message}</p>}
            </div>

            <div className="col-span-2">
              <label className="label-field">Your Subdomain</label>
              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>Passengers and drivers access your platform here</p>
              <div className="flex items-center gap-0 rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--input)' }}>
                <input
                  {...register('subdomain', {
                    required: 'Subdomain is required',
                    pattern: { value: /^[a-z0-9-]+$/, message: 'Lowercase letters, numbers and hyphens only' },
                    minLength: { value: 3, message: 'Minimum 3 characters' },
                  })}
                  placeholder="citycabs"
                  className="flex-1 px-3.5 py-2.5 text-sm bg-transparent outline-none"
                  style={{ color: 'var(--foreground)' }}
                />
                <span className="px-3 py-2.5 text-sm border-l flex-shrink-0" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', background: 'var(--muted)' }}>
                  .rideos.io
                </span>
              </div>
              {subdomainPreview && (
                <p className="text-xs mt-1.5 font-mono" style={{ color: 'var(--success)' }}>✓ {subdomainPreview}.rideos.io</p>
              )}
              {errors.subdomain && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.subdomain.message}</p>}
            </div>

            <div>
              <label className="label-field">Business Type</label>
              <select {...register('businessType')} className="input-field">
                {businessTypes.map((t) => <option key={`btype-${t}`} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <label className="label-field">Country</label>
              <select {...register('country')} className="input-field">
                {countries.map((c) => <option key={`country-${c}`} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="col-span-2">
              <label className="label-field">Phone Number</label>
              <input
                {...register('phone', { required: 'Phone is required' })}
                placeholder="+44 7700 900000"
                className={`input-field ${errors.phone ? 'error' : ''}`}
              />
              {errors.phone && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.phone.message}</p>}
            </div>

            <div className="col-span-2">
              <label className="label-field">Admin Email</label>
              <input
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
                placeholder="admin@yourcompany.com"
                className={`input-field ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.email.message}</p>}
            </div>

            <div>
              <label className="label-field">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })}
                  placeholder="••••••••"
                  className={`input-field pr-10 ${errors.password ? 'error' : ''}`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.password.message}</p>}
            </div>

            <div>
              <label className="label-field">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  {...register('confirmPassword', {
                    required: 'Please confirm password',
                    validate: (v) => v === password || 'Passwords do not match',
                  })}
                  placeholder="••••••••"
                  className={`input-field pr-10 ${errors.confirmPassword ? 'error' : ''}`}
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.confirmPassword.message}</p>}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setStep(1)} className="btn-secondary py-3 px-4">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Setting up...
                </>
              ) : (
                <>Continue to Payment <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Payment setup via Stripe */}
      {step === 3 && clientSecret && (
        <div className="fade-in">
          <div className="mb-6">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Payment Setup</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Add a payment method. You will not be charged during your 14-day trial.
            </p>
          </div>

          <div
            className="flex items-center justify-between p-4 rounded-xl mb-5"
            style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}
          >
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{selectedPlanObj?.name} Plan</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>14-day free trial, then {selectedPlanObj?.price}/mo</p>
            </div>
            <span className="font-mono font-bold text-lg" style={{ color: 'var(--primary)' }}>$0 today</span>
          </div>

          <Elements stripe={getStripe()} options={{ clientSecret, appearance: { theme: 'night', variables: { colorPrimary: '#f59e0b' } } }}>
            <StripePaymentForm clientSecret={clientSecret} onSuccess={handlePaymentSuccess} planName={selectedPlanObj?.name ?? 'Selected'} />
          </Elements>

          <button type="button" onClick={() => setStep(2)} className="btn-secondary w-full mt-3 py-2.5 text-sm flex items-center justify-center gap-2">
            <ChevronLeft className="w-4 h-4" /> Back to Details
          </button>
        </div>
      )}

      {/* Step 3 fallback: no client secret yet */}
      {step === 3 && !clientSecret && (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Setting up your account...</p>
        </div>
      )}

      {/* Step 4: Provisioning / Launch */}
      {step === 4 && (
        <div className="fade-in">
          {!provisionDone ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(245,158,11,0.15)' }}>
                  <Rocket className="w-8 h-8" style={{ color: 'var(--primary)' }} />
                </div>
                <h2 className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>Setting Up Your Platform</h2>
                <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  We&apos;re provisioning your complete ride-hailing system. This takes about 20 seconds.
                </p>
              </div>

              <div className="rounded-xl border p-5 space-y-3" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                {PROVISION_STEPS.map((ps, i) => {
                  const PIcon = ps.icon;
                  const isDone = provisionStep > i;
                  const isActive = provisionStep === i;
                  return (
                    <div key={ps.id} className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                        style={{
                          background: isDone ? '#10b981' : isActive ? 'var(--primary)' : 'var(--muted)',
                          color: isDone || isActive ? '#000' : 'var(--muted-foreground)',
                        }}
                      >
                        {isDone ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : isActive ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <PIcon className="w-4 h-4" />
                        )}
                      </div>
                      <span
                        className="text-sm"
                        style={{ color: isDone || isActive ? 'var(--foreground)' : 'var(--muted-foreground)' }}
                      >
                        {ps.label}
                      </span>
                      {isDone && <CheckCircle className="w-4 h-4 text-emerald-500 ml-auto" />}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-6 fade-in">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(16,185,129,0.15)' }}>
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Your Platform is Ready! 🎉</h2>
                <p className="text-sm mt-2" style={{ color: 'var(--muted-foreground)' }}>
                  {formData?.companyName || 'Your company'} is live on RideOS. Sign in to access your back office.
                </p>
              </div>

              <div className="rounded-xl border p-4 space-y-3" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                <p className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>YOUR PLATFORM URLS</p>
                {[
                  { label: 'Back Office', url: `https://${subdomainPreview}.rideos.io/company-admin-dashboard`, icon: Server },
                  { label: 'Passenger App', url: `https://app.${subdomainPreview}.rideos.io`, icon: Globe },
                  { label: 'Driver App', url: `https://driver.${subdomainPreview}.rideos.io`, icon: Globe },
                ].map(item => {
                  const ItemIcon = item.icon;
                  return (
                    <div key={item.label} className="flex items-center justify-between gap-3 p-3 rounded-lg" style={{ background: 'var(--muted)' }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <ItemIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                        <div className="min-w-0">
                          <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{item.label}</p>
                          <p className="text-xs font-mono truncate" style={{ color: 'var(--muted-foreground)' }}>{item.url}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <p className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>NEXT STEPS IN YOUR BACK OFFICE</p>
                {[
                  'Connect your custom domain (Domain & DNS settings)',
                  'Add your payment gateway keys (Settings → Payments)',
                  'Configure fares and pricing rules',
                  'Invite your first drivers',
                  'Customise your passenger & driver app branding',
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ background: 'var(--primary)', color: '#000' }}>{i + 1}</span>
                    <p className="text-xs" style={{ color: 'var(--foreground)' }}>{step}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={() => router.push('/company-admin-dashboard')}
                className="btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
              >
                <Rocket className="w-4 h-4" />
                Go to My Back Office
              </button>
              <button
                onClick={onSwitchToSignIn}
                className="btn-secondary w-full py-2.5 text-sm"
              >
                Sign In Instead
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sign in link */}
      {step < 3 && (
        <p className="mt-6 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Already have an account?{' '}
          <button onClick={onSwitchToSignIn} className="font-medium hover:underline" style={{ color: 'var(--primary)' }}>
            Sign in
          </button>
        </p>
      )}
    </div>
  );
}